import {
  SkyClubCard,
  SkyClubSpendTracker
} from '@com-delta-idp/mnx-skymiles-overview-ui';
import {
  AEMErrorMessage,
  UserSession,
  checkErrors,
  isDisplay,
  isError,
  isValidData
} from '@com-delta-idp/mnx-skymiles-shared';
import { logError } from '@com-delta-idp/mnx-utils';
import { SkyClubResponse } from '../models';
import { VisitSpend } from '../models/aem/visit-spend.interface';
import { StatusTrackerResponse } from '../models/api/tracker.interfaces';
import {
  getCards,
  isValidSkyClubMember
} from './amex-skyclub-tracker-card.adapter';
import { checkEmptyError } from './amex-skyclub-tracker-helper.adapter';

type TrackerInputs = {
  aem: VisitSpend | undefined;
  skyClub: SkyClubResponse | undefined;
  spendTracker: StatusTrackerResponse | undefined;
  profile: UserSession | undefined;
  featureFlag: boolean | undefined;
  errors: (string | undefined)[];
  aemErrorMessages: AEMErrorMessage[] | undefined;
};

export const getAmexSkyClubTracker = ({
  aem,
  skyClub,
  spendTracker,
  profile,
  featureFlag,
  errors,
  aemErrorMessages
}: TrackerInputs): SkyClubSpendTracker => {
  try {
    // Early return if there are errors
    if (checkErrors(errors, aemErrorMessages)) {
      return isError<SkyClubSpendTracker>(
        'skyclub-tracker',
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }

    // Check if all required data is valid
    const hasValidData = isValidData<
      VisitSpend,
      [
        VisitSpend | undefined,
        UserSession | undefined,
        SkyClubResponse | undefined,
        StatusTrackerResponse | undefined,
        boolean | undefined
      ]
    >('zeroVisitLabel', aem, profile, skyClub, spendTracker, featureFlag);

    if (!hasValidData) {
      return isDisplay<SkyClubSpendTracker>(featureFlag);
    }
    // At this point, we know all data is valid, so we can safely assert types
    return adaptAmexSkyClubTrackerObject(
      aem!,
      skyClub!,
      spendTracker!,
      profile!,
      featureFlag!,
      aemErrorMessages!
    );
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'OVERVIEW', 'AMEX_SKYCLUB_TRACKER');
    return isError<SkyClubSpendTracker>(
      'skyclub-tracker',
      aemErrorMessages,
      true
    );
  }
};

export const adaptAmexSkyClubTrackerObject = (
  aem: VisitSpend,
  skyClub: SkyClubResponse,
  spendTracker: StatusTrackerResponse,
  profile: UserSession,
  featureFlag: boolean,
  aemErrorMessages: AEMErrorMessage[]
): SkyClubSpendTracker => {
  if (checkMemberAndValidCard(aem, profile, featureFlag)) {
    const cardsData: SkyClubCard[] = getCards(
      aem,
      skyClub,
      spendTracker,
      profile
    );
    if (cardsData.length === 0) {
      return checkEmptyError(
        skyClub,
        spendTracker,
        profile,
        featureFlag,
        aemErrorMessages
      );
    }
    return {
      headerLogo: {
        alt: aem.header.alt,
        src: aem.header.src
      },
      cards: cardsData,
      disabled: !featureFlag,
      loaded: true
    };
  } else {
    return isDisplay<SkyClubSpendTracker>(false);
  }
};

export const checkSkyClub = (
  trackerContent: StatusTrackerResponse,
  skyClub: SkyClubResponse
) => {
  return 'dscSpendTracker' in trackerContent && skyClub.visitRecord.length > 0;
};

export const checkMemberAndValidCard = (
  aem: VisitSpend,
  profile: UserSession,
  featureFlag: boolean
): boolean => {
  if (!featureFlag) return false;

  // Early return if user is a valid SkyClub member
  if (isValidSkyClubMember(profile)) return true;

  // Use some() instead of map() for better performance
  return aem.cardTile.some((card) =>
    profile.cobrandCardtypeList.includes(card.type)
  );
};
